import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { chatService } from '../services/chatService';
import { logger } from '../utils/logger';
import { sessionDocToStreamEvent } from '../utils/sessionDocStreamEvent';
import { CHAT_STORAGE_KEYS, getUserPreferenceStorageKeys } from '../constants/storageKeys';
import { useBrowserAuth } from './AuthContext';
import { 
  Session, 
  AuthState, 
  UseSessionReturn,
  SessionErrorInfo,
  ChatMessageDetail,
  User,
} from '../types';

interface SessionContextType extends UseSessionReturn {
  selectedFleetId: number | null;
  setSelectedFleetId: (fleetId: number | null) => void;
  customerName: string | null;
  selectedFleetName: string | null;
  pendingResumeMessages: any[] | null;
  clearPendingResumeMessages: () => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

const moduleLogger = logger.getModule('SessionContext');

interface PersistedSession extends Session {
  authenticatedUserId: string;
}

interface SessionCustomerSelection {
  customerId: string;
  clearPersistedCustomer: boolean;
}

export const resolveSessionCustomer = (
  requestedCustomerId: string | undefined,
  persistedCustomerId: string,
  user: User,
): SessionCustomerSelection => {
  if (requestedCustomerId) {
    return {
      customerId: requestedCustomerId,
      clearPersistedCustomer: false,
    };
  }

  const accessibleCustomers = user.customers;
  const persistedCustomerIsAccessible =
    persistedCustomerId
    && (
      accessibleCustomers === undefined
      || accessibleCustomers.some(
        (customer) => String(customer.id) === persistedCustomerId
      )
    );

  if (persistedCustomerIsAccessible) {
    return {
      customerId: persistedCustomerId,
      clearPersistedCustomer: false,
    };
  }

  return {
    customerId: user.customerId || '',
    clearPersistedCustomer: Boolean(persistedCustomerId),
  };
};

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const browserAuth = useBrowserAuth();
  const [session, setSession] = useState<Session | null>(null);
  const [sessionOwnerId, setSessionOwnerId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUserInitiatedSessionLoading, setIsUserInitiatedSessionLoading] = useState(false);
  const [selectedFleetId, setSelectedFleetId] = useState<number | null>(null);
  const [sessionError, setSessionError] = useState<SessionErrorInfo | null>(null);
  const [pendingResumeMessages, setPendingResumeMessages] = useState<any[] | null>(null);
  const [isSessionBootstrapping, setIsSessionBootstrapping] = useState(true);
  const sessionBootstrapKeyRef = useRef<string | null>(null);

  const authState: AuthState = {
    isAuthenticated: browserAuth.authenticated && Boolean(browserAuth.user),
    isLoading: browserAuth.loading,
    error: browserAuth.error,
    user: browserAuth.user,
    token: null,
    refreshToken: null,
    tokenExpiry: browserAuth.expiresAt,
  };
  const authenticatedUserId = browserAuth.authenticated ? browserAuth.user?.id || null : null;
  const visibleSession = authenticatedUserId && sessionOwnerId === authenticatedUserId
    ? session
    : null;
  const visibleSelectedFleetId = visibleSession ? selectedFleetId : null;
  const visiblePendingResumeMessages = visibleSession ? pendingResumeMessages : null;

  // Computed values
  const customerName = visibleSession?.customerName || null;
  const selectedFleetName = visibleSelectedFleetId && visibleSession?.fleets
    ? visibleSession.fleets.find(fleet => fleet.id === visibleSelectedFleetId)?.name || null
    : null;

  useEffect(() => {
    if (browserAuth.loading) {
      return;
    }

    if (!browserAuth.authenticated || !browserAuth.user) {
      setIsSessionBootstrapping(false);
      sessionBootstrapKeyRef.current = null;
      return;
    }

    const handoffSource = isTiercHandoffFromUrl() ? 'tierc' : 'standard';
    const bootstrapKey = `${browserAuth.user.id}:${handoffSource}`;
    if (sessionBootstrapKeyRef.current === bootstrapKey) {
      return;
    }
    sessionBootstrapKeyRef.current = bootstrapKey;
    setIsSessionBootstrapping(true);
    localStorage.removeItem(CHAT_STORAGE_KEYS.lastCustomerId);
    localStorage.removeItem(CHAT_STORAGE_KEYS.lastFleetId);

    const initializeChatSession = async () => {
      if (handoffSource === 'tierc') {
        await resumeChatSession();
      } else {
        await restoreChatSession();
      }
    };

    initializeChatSession()
      .catch((error) => moduleLogger.error('Chat session initialization failed', error))
      .finally(() => setIsSessionBootstrapping(false));
  }, [browserAuth.authenticated, browserAuth.loading, browserAuth.user]); // eslint-disable-line react-hooks/exhaustive-deps

  const isTiercHandoffFromUrl = (): boolean => {
    try {
      return new URLSearchParams(window.location.search).get('source') === 'tierc';
    } catch {
      return false;
    }
  };

  const clearTiercHandoffFromUrl = () => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('source') !== 'tierc') {
        return;
      }
      url.searchParams.delete('source');
      window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
    } catch (error) {
      moduleLogger.warn('Failed to clear Tier C handoff marker from URL', { error });
    }
  };

  const stripContext = (content: string): string => {
    return content.replace(/^context:\s*\{[\s\S]*?\}\s*\n\n/m, '').trim();
  };

  const mapMessageDetailToChatMessage = (msg: ChatMessageDetail) => {
    const sessionDocEvents = (msg.session_docs || []).map(sessionDocToStreamEvent);

    return {
      id: msg.id.toString(),
      messageId: msg.id,
      role: msg.role as 'user' | 'assistant' | 'system',
      content: msg.role === 'user' ? stripContext(msg.content) : msg.content,
      sender: msg.role === 'user' ? 'You' : 'SpeedGauge',
      direction: msg.role === 'user' ? 'outgoing' as const : 'incoming' as const,
      timestamp: msg.created_at,
      localTimestamp: new Date(msg.created_at).toLocaleTimeString(),
      metadata: sessionDocEvents.length > 0
        ? { streamEvents: sessionDocEvents, streamEventType: 'response', streamPhase: 'complete' }
        : undefined,
    };
  };

  const persistSession = (sessionToPersist: Session, authenticatedUserId: string) => {
    const persistedSession: PersistedSession = {
      ...sessionToPersist,
      authenticatedUserId,
    };
    localStorage.setItem(CHAT_STORAGE_KEYS.session, JSON.stringify(persistedSession));
  };

  const logout = async () => {
    setIsLoading(true);
    
    try {
      // Clean up chat session
      if (session) {
        await chatService.deleteSession(session.sessionId);
      }
      
      await browserAuth.logout();
    } catch (error) {
      moduleLogger.error('Logout cleanup failed', error);
    } finally {
      // Clear cached chat data to prevent old messages appearing after reauthentication.
      localStorage.removeItem(CHAT_STORAGE_KEYS.session);
      
      // SessionStorage - chat messages and session tracking
      // These must be cleared to prevent old chat messages from flashing on re-login
      try {
        sessionStorage.removeItem(CHAT_STORAGE_KEYS.messages);
        sessionStorage.removeItem(CHAT_STORAGE_KEYS.sessionId);
        sessionStorage.removeItem(CHAT_STORAGE_KEYS.authenticatedUserId);
        moduleLogger.debug('Cleared chat sessionStorage keys on logout');
      } catch (error) {
        moduleLogger.warn('Failed to clear sessionStorage', { error });
      }
      
      setSession(null);
      setSessionOwnerId(null);
      setSelectedFleetId(null); // Reset fleet selection on logout
      setIsLoading(false);

      // Customer/fleet selections are user preference and intentionally preserved.
      
      sessionBootstrapKeyRef.current = null;
    }
  };

  // Check if an error is a network/connection error (server unreachable)
  const isNetworkError = (error: any): boolean => {
    const message = (error?.message || '').toLowerCase();
    const code = error?.code || error?.response?.status;
    
    return message.includes('network error') ||
           message.includes('err_connection_refused') ||
           message.includes('err_network') ||
           message.includes('econnrefused') ||
           message.includes('enotfound') ||
           message.includes('connection refused') ||
           code === 'ERR_NETWORK' ||
           code === 'ERR_CONNECTION_REFUSED';
  };

  // Check if an error is a server error (5xx status)
  const isServerError = (error: any): boolean => {
    const status = error?.response?.status;
    return status && status >= 500;
  };

  const createChatSession = async (
    customerId?: string,
    fleetId?: number | null,
    user?: any,
    _token?: string,
    userInitiated: boolean = false,
  ) => {
    moduleLogger.debug('Starting createChatSession', { customerId, fleetId, userInitiated });
    
    // Clear any previous session errors before attempting to create
    setSessionError(null);
    
    if (userInitiated) {
      setIsUserInitiatedSessionLoading(true);
    }
    
    // Browser authentication is represented by the resolved user, not a JWT.
    const sessionUser = user || authState.user;
    
    moduleLogger.debug('User object', { user: sessionUser });
    moduleLogger.debug('Available customerId sources', {
      provided: customerId,
      fromUser: sessionUser?.customerId,
      authStateUser: authState.user?.customerId
    });
    
    if (!sessionUser) {
      moduleLogger.error('Cannot create session: no authenticated user', {
        hasUser: !!sessionUser,
        sessionUser,
        authStateUser: authState.user
      });
      return;
    }
    
    try {
      const preferenceStorageKeys = getUserPreferenceStorageKeys(sessionUser.id);
      // Use provided customerId or fall back to user's default
      // Or empty string if none available
      const persistedCustomerId = localStorage.getItem(preferenceStorageKeys.lastCustomerId) || '';
      const customerSelection = resolveSessionCustomer(
        customerId,
        persistedCustomerId,
        sessionUser,
      );
      const sessionCustomerId = customerSelection.customerId;
      if (customerSelection.clearPersistedCustomer) {
        localStorage.removeItem(preferenceStorageKeys.lastCustomerId);
      }

      const currentSessionCustomerId = session?.customerId ? String(session.customerId) : '';
      const isCustomerSwitch =
        Boolean(customerId)
        && Boolean(currentSessionCustomerId)
        && customerId !== currentSessionCustomerId;

      const persistedFleetId = localStorage.getItem(preferenceStorageKeys.lastFleetId);
      const parsedPersistedFleetId = persistedFleetId ? parseInt(persistedFleetId, 10) : NaN;
      const requestedFleetId =
        isCustomerSwitch
          ? undefined
          : typeof fleetId === 'number'
            ? fleetId
            : Number.isNaN(parsedPersistedFleetId)
              ? undefined
              : parsedPersistedFleetId;
      
      moduleLogger.debug('Creating session for customerId', {
        sessionCustomerId,
        requestedFleetId,
        isCustomerSwitch,
      });
      
      const sessionResponse = await chatService.createSession(sessionCustomerId, requestedFleetId);
      moduleLogger.debug('Session created successfully', sessionResponse);
      
      // Convert response to Session format
      const newSession = {
        sessionId: sessionResponse.session_id,
        customerId: String(sessionResponse.customer_id),
        customerName: sessionResponse.customer_name,
        fleets: sessionResponse.fleets,
        createdAt: sessionResponse.created_at,
        expiresAt: sessionResponse.expires_at,
        selectedFleetId: sessionResponse.fleet_id,
        isSuperuser: sessionResponse.is_superuser,
        permissions: sessionResponse.permissions,
        history: sessionResponse.session_history, // Map session_history to history
      };
      
      moduleLogger.debug('Setting session state', { session: newSession });
      setSession(newSession);
      setSessionOwnerId(sessionUser.id);
      persistSession(newSession, sessionUser.id);
      localStorage.setItem(preferenceStorageKeys.lastCustomerId, newSession.customerId);
      
      // Honor selected fleet returned by server, then fall back to "All Vehicles", then first fleet.
      const selectedFleet =
        typeof sessionResponse.fleet_id === 'number'
          ? sessionResponse.fleets?.find((f) => f.id === sessionResponse.fleet_id)
          : undefined;
      const allVehiclesFleet = sessionResponse.fleets?.find(f => f.name === 'All Vehicles');
      const defaultFleet = selectedFleet || allVehiclesFleet || sessionResponse.fleets?.[0];

      if (defaultFleet) {
        setSelectedFleetId(defaultFleet.id);
        localStorage.setItem(preferenceStorageKeys.lastFleetId, String(defaultFleet.id));
        moduleLogger.debug('Default fleet set', { fleetId: defaultFleet.id, fleetName: defaultFleet.name });
      } else {
        setSelectedFleetId(null);
        localStorage.removeItem(preferenceStorageKeys.lastFleetId);
      }
      
      moduleLogger.debug('Session fully initialized');
    } catch (error: any) {
      moduleLogger.error('Chat session creation failed', error);
      
      // Determine error type and create user-friendly message
      const errorMessage = error?.message || 'Failed to create chat session';
      const netError = isNetworkError(error);
      const srvError = isServerError(error);
      
      moduleLogger.debug('Error type detection', {
        isNetworkError: netError,
        isServerError: srvError,
        status: error?.response?.status,
        code: error?.code,
        message: error?.message
      });
      
      // Create session error info
      // Prioritize server error over network error (more specific)
      const errorInfo: SessionErrorInfo = {
        message: srvError
          ? 'The chat server is currently experiencing issues. Please try again in a few moments.'
          : netError 
          ? 'Unable to connect to the chat server. Please verify the network connection and try again.'
          : errorMessage,
        isNetworkError: netError && !srvError,  // Only network if NOT server
        isServerError: srvError,
        timestamp: Date.now()
      };
      
      setSessionError(errorInfo);
      moduleLogger.error('Stored session error', errorInfo);
      
      throw error;
    } finally {
      if (userInitiated) {
        setIsUserInitiatedSessionLoading(false);
      }
    }
  };

  const resumeChatSession = async (): Promise<boolean> => {
    moduleLogger.debug('Starting resumeChatSession');
    setSessionError(null);
    const currentUserId = browserAuth.user!.id;
    const preferenceStorageKeys = getUserPreferenceStorageKeys(currentUserId);

    try {
      const sessionResponse = await chatService.resumeSession();
      const resumedSession = {
        sessionId: sessionResponse.session_id,
        customerId: String(sessionResponse.customer_id),
        customerName: sessionResponse.customer_name,
        fleets: sessionResponse.fleets,
        createdAt: sessionResponse.created_at,
        expiresAt: sessionResponse.expires_at,
        selectedFleetId: sessionResponse.fleet_id,
        isSuperuser: sessionResponse.is_superuser,
        permissions: sessionResponse.permissions,
        history: sessionResponse.session_history,
      };

      setSession(resumedSession);
      setSessionOwnerId(currentUserId);
      persistSession(resumedSession, currentUserId);
      localStorage.setItem(preferenceStorageKeys.lastCustomerId, resumedSession.customerId);

      if (typeof sessionResponse.fleet_id === 'number') {
        setSelectedFleetId(sessionResponse.fleet_id);
        localStorage.setItem(preferenceStorageKeys.lastFleetId, String(sessionResponse.fleet_id));
      } else {
        setSelectedFleetId(null);
        localStorage.removeItem(preferenceStorageKeys.lastFleetId);
      }

      setPendingResumeMessages(
        (sessionResponse.messages || []).map(mapMessageDetailToChatMessage)
      );
      clearTiercHandoffFromUrl();
      moduleLogger.debug('Session resumed successfully', {
        sessionId: sessionResponse.session_id,
        messageCount: sessionResponse.messages?.length || 0,
      });
      return true;
    } catch (error: any) {
      moduleLogger.error('Session resume failed', error);
      const resumeStatus = error?.response?.status;
      const handoffUnavailable = resumeStatus === 403 || resumeStatus === 404;

      setSession(null);
      setSessionOwnerId(null);
      setSelectedFleetId(null);
      setPendingResumeMessages(null);
      localStorage.removeItem(CHAT_STORAGE_KEYS.session);
      localStorage.removeItem(preferenceStorageKeys.lastCustomerId);
      localStorage.removeItem(preferenceStorageKeys.lastFleetId);
      setSessionError({
        message: handoffUnavailable
          ? 'This session link has expired or was already used. Return to the SpeedGauge Portal to open a new chat session.'
          : 'Unable to resume this session. Return to the SpeedGauge Portal to open a new chat session.',
        isNetworkError: isNetworkError(error),
        isServerError: isServerError(error),
        isHandoffError: true,
        timestamp: Date.now(),
      });
      return false;
    }
  };

  const restoreChatSession = async () => {
    try {
      const preferenceStorageKeys = getUserPreferenceStorageKeys(browserAuth.user!.id);
      const savedSession = localStorage.getItem(CHAT_STORAGE_KEYS.session);
      if (savedSession) {
        const sessionData = JSON.parse(savedSession) as PersistedSession;
        if (sessionData.authenticatedUserId !== browserAuth.user?.id) {
          moduleLogger.info('Discarding cached session owned by a different authenticated user');
          localStorage.removeItem(CHAT_STORAGE_KEYS.session);
        } else {
        
          // Check if session is still valid (not expired)
          const currentTime = Math.floor(Date.now() / 1000); // Convert to seconds
          if (sessionData.expiresAt > currentTime) {
            setSession(sessionData);
            setSessionOwnerId(sessionData.authenticatedUserId);
            // Set default fleet selection to "All Vehicles" when restoring session
            const savedFleetIdRaw = localStorage.getItem(preferenceStorageKeys.lastFleetId);
            const savedFleetId = savedFleetIdRaw ? parseInt(savedFleetIdRaw, 10) : NaN;
            const savedFleet = Number.isNaN(savedFleetId)
              ? undefined
              : sessionData.fleets?.find((f: any) => f.id === savedFleetId);
            const allVehiclesFleet = sessionData.fleets?.find((f: any) => f.name === 'All Vehicles');
            const defaultFleet = savedFleet || allVehiclesFleet || sessionData.fleets?.[0];
            setSelectedFleetId(defaultFleet?.id || null);

            if (sessionData.customerId) {
              localStorage.setItem(preferenceStorageKeys.lastCustomerId, String(sessionData.customerId));
            }
            return;
          } else {
            moduleLogger.debug('Saved session has expired, removing');
            localStorage.removeItem(CHAT_STORAGE_KEYS.session);
          }
        }
      }
      
      // Create new session if no valid session found
      await createChatSession();
    } catch (error) {
      moduleLogger.error('Session restoration failed', error);
    }
  };

  const retryCreateSession = async () => {
    moduleLogger.debug('Retrying session creation');
    
    if (!authState.user) {
      moduleLogger.error('Cannot retry session creation: no authenticated user');
      return;
    }
    
    try {
      await createChatSession(authState.user.customerId, undefined, authState.user);
    } catch (error) {
      moduleLogger.error('Session creation retry failed', error);
      // Error is already tracked in sessionError state by createChatSession
    }
  };

  const updateSelectedFleetId = (fleetId: number | null) => {
    setSelectedFleetId(fleetId);
    if (!authenticatedUserId) {
      return;
    }

    const preferenceStorageKeys = getUserPreferenceStorageKeys(authenticatedUserId);

    if (typeof fleetId === 'number') {
      localStorage.setItem(preferenceStorageKeys.lastFleetId, String(fleetId));
    } else {
      localStorage.removeItem(preferenceStorageKeys.lastFleetId);
    }
  };

  const clearPendingResumeMessages = React.useCallback(() => {
    setPendingResumeMessages(null);
  }, []);

  return (
    <SessionContext.Provider value={{
      authState,
      session: visibleSession,
      logout,
      createChatSession,
      isLoading,
      isUserInitiatedSessionLoading,
      selectedFleetId: visibleSelectedFleetId,
      setSelectedFleetId: updateSelectedFleetId,
      customerName,
      selectedFleetName,
      sessionError,
      retryCreateSession,
      pendingResumeMessages: visiblePendingResumeMessages,
      clearPendingResumeMessages,
    }}>
      {children}
    </SessionContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useAuth must be used within SessionProvider');
  }
  return context;
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within SessionProvider');
  }
  return context;
};
